import React, { useEffect, useMemo, useState } from 'react'

import { useQuery } from '@apollo/client'
import { BlankSlate, Box, Spinner } from '@factorialco/deprecated-design-system'
import { TwoColumnLayout } from '@factorialco/factorial-one-react'
import { FormFormEditorDocument, FormsElementTypeEnum } from 'generated/resources/graphql'

import Loading from 'modules/core/components/Loading'

import { handleResult } from '../../core/lib/handleMutation'
import i18n from '../../core/lib/i18n'
import app from '../../core/store/models/app'
import useCreateElement from '../hooks/useCreateElement'
import {
  elementFromData,
  formFromData,
  getDefaultOptions,
  useCreateForm,
  useUpdateForm,
} from '../hooks/useForms'
import useUpdateElement from '../hooks/useUpdateElement'
import { Element } from '../lib/Element'
import { Form } from '../lib/Form'
import { SectionElement } from '../lib/elements/SectionElement'
import SidebarForm from './SidebarForm'
import ElementEditor from './editor/ElementEditor'
import { EmptyState } from './editor/EmptyState'
import DeleteDialog from './modals/DeleteDialog'
import { ElementPreview } from './preview/ElementPreview'

interface FormEditorProps {
  formId: string
  onSave?: (form: Form) => void
  resourceType: string
  resourceId: string
}

/**
 * FormEditor component for creating and editing forms
 * Allows adding different types of elements to a form
 */
const FormEditor: React.FC<FormEditorProps> = ({ formId, resourceId, resourceType }) => {
  // Initialize form state with initial form or create a new one
  const { data: backendData, loading: loadingForm } = useQuery(FormFormEditorDocument, {
    variables: {
      id: formId ?? '',
      // resourceId: resourceId,
      resourceType: resourceType ?? '',
    },
    skip: !formId && !resourceType,
  })

  const formData = backendData?.forms.form

  const form = useMemo(() => {
    return !loadingForm && formData
      ? formFromData({
          ...formData,
          description: formData.description ?? '',
          elements: formData.elements.map((el) => ({ ...el, description: el.description ?? '' })),
        })
      : null
  }, [loadingForm, formData])

  const [selectedElement, setSelectedElement] = useState<Element | null>(null)
  const [blocking, blockForm] = useState(false)
  const [openDialog, setOpenDialog] = useState<string>('')

  useEffect(() => {
    let elementToSelect: Element | null | undefined = null
    if (selectedElement && form) {
      elementToSelect = form.getElement(selectedElement.id)
    }
    if (!elementToSelect && form && form.elements.length > 0) {
      elementToSelect = form.elements[0]
    }
    if (elementToSelect) {
      setSelectedElement(elementToSelect)
    }
  }, [form, selectedElement])

  // called when order changed in sidebar to store new positions
  const { updateForm } = useUpdateForm()
  const onFormUpdated = async () => {
    if (form) {
      try {
        blockForm(true)
        await updateForm(form)
      } finally {
        blockForm(false)
      }
    }
  }

  // This line is to prevent roomba from deleting 'useCreateForm' function
  const { createForm } = useCreateForm()
  const onCreateForm = async () => {
    if (form) {
      await createForm(form, 0, 0)
    }
  }

  const { create } = useCreateElement()
  const { update } = useUpdateElement()

  const elementUpdated = async (element: Element) => {
    try {
      blockForm(true)
      const response = await update({
        ...element.toBackend(),
        id: element.id ?? '',
        resourceId,
        resourceType,
        sectionId: element.sectionId || undefined,
      })

      handleResult(response, () => {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('common.error'),
        })
      })
    } finally {
      blockForm(false)
    }
  }

  const elementDelete = (element: Element) => {
    setOpenDialog(element.id)
  }

  if (loadingForm) {
    return <Loading expand />
  }
  if (!form) {
    return (
      <Box height="full" justifyContent="center" alignItems="center">
        <BlankSlate
          illustration="sign-post"
          title={i18n.t('forms.editor.not_found.title')}
          description={i18n.t('forms.editor.not_found.description')}
        />
      </Box>
    )
  }

  //this is the function to create new elements in the form or inside a section
  const createElement = async (type: FormsElementTypeEnum, sectionId: string | null) => {
    const section = sectionId ? form.getElement(sectionId) : undefined
    const count = section instanceof SectionElement ? section.elements.length : form.elements.length
    const label =
      type === 'section'
        ? i18n.t('forms.editor.section_position_title', { position: count + 1 })
        : i18n.t('forms.editor.question_position_title', { position: count + 1 })
    const element = elementFromData({
      type,
      label,
      description: '',
      mandatory: false,
      options: getDefaultOptions(type),
      params: '{}',
      position: count,
      sectionId: sectionId ?? undefined,
    })
    try {
      blockForm(true)
      const response = await create({
        ...element.toBackend(),
        sectionId: element.sectionId,
        formId: form.id,
        resourceId: form.resourceId,
        resourceType: form.resourceType,
      })

      const result = handleResult(response, () => {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('common.error'),
        })
      })

      if (result.success && result.data.element) {
        // Update the element with the id generated in backend to be able to select automatically the new element in sidebar
        element.id = result.data.element.id
        setSelectedElement(element)
      }
    } finally {
      blockForm(false)
    }
  }

  const elementDuplicate = async (element: Element) => {
    const section = element.sectionId ? form.getElement(element.sectionId) : undefined
    element.position =
      section instanceof SectionElement ? section.elements.length : form.elements.length
    //remove option ids, so they will be created instead of updated
    element.options.forEach((option) => (option.id = undefined))

    try {
      blockForm(true)
      const response = await create({
        ...element.toBackend(),
        label: i18n.t('forms.modals.actions.duplicate_label', { label: element.label }),
        sectionId: element.sectionId,
        formId: form.id,
        resourceId: form.resourceId,
        resourceType: form.resourceType,
      })

      const result = handleResult(response, () => {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('common.error'),
        })
      })

      if (result.success && result.data.element) {
        // Update the element with the id generated in backend to be able to select automatically the new element in sidebar
        element.id = result.data.element.id
        setSelectedElement(element)
      }
    } finally {
      blockForm(false)
    }
  }

  if (form.getElementCount() === 0) return <EmptyState onAddElement={createElement} />
  return (
    <Box position="relative">
      {blocking && (
        <Box
          alignItems="center"
          justifyContent="center"
          style={{
            position: 'absolute',
            top: '0',
            left: '0',
            right: '0',
            bottom: '0',
            zIndex: '1',
            background: 'rgba(255, 255, 255, 0.5)',
          }}
        >
          <Spinner />
        </Box>
      )}
      <TwoColumnLayout
        mainColumnPosition="right"
        sideContent={
          <SidebarForm
            form={form}
            resourceId={resourceId}
            resourceType={resourceType}
            onFormUpdated={onFormUpdated}
            selectedElementId={selectedElement?.id || null}
            onSelectElement={setSelectedElement}
            onAddElement={createElement}
            onDuplicateElement={elementDuplicate}
          />
        }
      >
        <Box gap="s24">
          {selectedElement && !selectedElement.isLocked() && (
            <ElementEditor
              element={selectedElement}
              form={form}
              onChange={elementUpdated}
              onDelete={elementDelete}
            >
              {selectedElement.getEditorComponent(elementUpdated)}
            </ElementEditor>
          )}
          {selectedElement && selectedElement.isLocked() && (
            <ElementPreview element={selectedElement} form={form} />
          )}
        </Box>
      </TwoColumnLayout>
      <DeleteDialog
        open={Boolean(openDialog)}
        onClose={() => setOpenDialog('')}
        formId={form.id}
        elementId={openDialog}
        resourceId={resourceId}
        resourceType={resourceType}
      />
    </Box>
  )
}

export default FormEditor
